/**
 * Password policy — the UI mirror.
 *
 * **The server is the control.** This file exists so a person setting a
 * password sees the rules as they type instead of discovering them in a 400.
 * The authority is `app/auth/password_policy.py` (enforced in
 * `UserManager.validate_password`, which fastapi-users calls on register,
 * password reset and `PATCH /users/me`, plus the two direct-hash paths in
 * `admin.py` and `proposal_service.py`). Keep the two in sync; when they
 * disagree, the server wins and its `reason` is what the user must see.
 *
 * Deliberately a smaller blocklist than the server's: this is live feedback,
 * not enforcement, and shipping a full breach list to the browser buys
 * nothing the server does not already refuse.
 */

export const MIN_PASSWORD_LENGTH = 12
export const MAX_PASSWORD_LENGTH = 128

/** Shortest account-context token worth substring-matching (mirrors _MIN_CONTEXT_TOKEN). */
const MIN_CONTEXT_TOKEN = 4

const SYMBOL_LEET: Record<string, string> = { '@': 'a', $: 's', '!': 'i', '+': 't' }
const DIGIT_LEET: Record<string, string> = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't',
}

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz'
const DIGITS = '01234567890123456789'
const KEYBOARD_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', '1234567890']

/** The head of the server's list — the values people actually reach for. */
const COMMON_PASSWORDS = new Set([
  'password', 'passwd', 'pass', 'passw', 'mypassword', 'newpassword', 'changeme',
  'qwerty', 'qwertyuiop', 'qazwsx', 'asdf', 'asdfgh', 'asdfghjkl', 'zxcvbn', 'zxcvbnm',
  'iloveyou', 'letmein', 'welcome', 'admin', 'administrator', 'login', 'guest', 'root',
  'user', 'test', 'testing', 'temp', 'secret', 'trustno', 'access', 'master',
  'monkey', 'dragon', 'shadow', 'sunshine', 'princess', 'flower', 'superman', 'batman',
  'football', 'baseball', 'basketball', 'soccer', 'hockey', 'michael', 'jordan',
  'hunter', 'charlie', 'thomas', 'robert', 'daniel', 'chocolate', 'cookie', 'computer',
  'internet', 'freedom', 'forever', 'hello', 'summer', 'winter', 'spring', 'autumn',
  'purple', 'orange', 'yellow', 'silver', 'golden', 'diamond', 'money', 'apple',
  'claimshield', 'claimshieldpro', 'trucking', 'truck', 'trucker', 'driver', 'drivers',
  'safety', 'safetyfirst', 'compliance', 'fmcsa', 'portal', 'sentinel',
])

function translate(value: string, table: Record<string, string>): string {
  return value.replace(/./g, (char) => table[char] ?? char)
}

/** Normalised forms of the password, mirroring `_candidates` in the Python module. */
function candidates(password: string): string[] {
  const lowered = password.toLowerCase()
  const seeds = new Set(
    [lowered, translate(lowered, SYMBOL_LEET)].map((s) => s.replace(/[^a-z0-9]/g, '')),
  )
  const forms = new Set<string>()
  seeds.forEach((seed) => {
    if (!seed) return
    forms.add(seed)
    const stripped = seed.replace(/\d+$/, '').replace(/^\d+/, '')
    if (stripped) forms.add(stripped)
  })
  Array.from(forms).forEach((form) => {
    const translated = translate(form, DIGIT_LEET)
    if (translated) forms.add(translated)
    const trailingStripped = translate(form.replace(/\d+$/, ''), DIGIT_LEET)
    if (trailingStripped) forms.add(trailingStripped)
  })
  return Array.from(forms)
}

function isARun(password: string): boolean {
  const flat = password.toLowerCase().replace(/[^a-z0-9]/g, '')
  if (flat.length < 4) return false
  const reversed = flat.split('').reverse().join('')
  return [ALPHABET, DIGITS, ...KEYBOARD_ROWS].some(
    (sequence) => sequence.includes(flat) || sequence.includes(reversed),
  )
}

function contextTokens(email?: string, firstName?: string, lastName?: string): string[] {
  const tokens = new Set(['claimshield', 'claimshieldpro'])
  if (email && email.includes('@')) {
    const [local = '', domain = ''] = email.toLowerCase().split('@')
    tokens.add(local.replace(/[^a-z0-9]/g, ''))
    ;(local.match(/[a-z]{4,}/g) ?? []).forEach((run) => tokens.add(run))
    tokens.add(domain.split('.')[0] ?? '')
  } else if (email) {
    tokens.add(email.toLowerCase().replace(/[^a-z0-9]/g, ''))
  }
  ;[firstName, lastName].forEach((name) => {
    if (name) tokens.add(name.toLowerCase().replace(/[^a-z0-9]/g, ''))
  })
  return Array.from(tokens).filter((t) => t.length >= MIN_CONTEXT_TOKEN)
}

export interface PasswordContext {
  email?: string
  firstName?: string
  lastName?: string
}

export interface PasswordRule {
  /** Stable key, so a caller can style or test one rule. */
  key: 'length' | 'common' | 'pattern' | 'context'
  /** What the user has to do — phrased as the requirement, not the failure. */
  label: string
  met: boolean
}

/**
 * Evaluate a password against the mirrored policy.
 *
 * Rules read as a checklist (all `met` → acceptable). An empty password
 * returns every rule unmet without shouting at someone who hasn't typed yet;
 * the caller decides when to show it.
 */
export function evaluatePassword(password: string, context: PasswordContext = {}): PasswordRule[] {
  const lowered = password.toLowerCase()
  const forms = candidates(password)
  const matchedContext = contextTokens(context.email, context.firstName, context.lastName)
    .some((token) => lowered.includes(token))

  return [
    {
      key: 'length',
      label: `At least ${MIN_PASSWORD_LENGTH} characters`,
      met: password.length >= MIN_PASSWORD_LENGTH && password.length <= MAX_PASSWORD_LENGTH,
    },
    {
      key: 'common',
      label: 'Not a common password',
      met: password.length > 0 && !forms.some((form) => COMMON_PASSWORDS.has(form)),
    },
    {
      key: 'pattern',
      label: 'No keyboard runs or repeated characters',
      met: password.length > 0 && new Set(lowered).size > 2 && !isARun(password),
    },
    {
      key: 'context',
      label: 'Nothing from your name, email, or “ClaimShield”',
      met: password.length > 0 && !matchedContext,
    },
  ]
}

/** True when every mirrored rule passes. The server still has the last word. */
export function passwordMeetsPolicy(password: string, context: PasswordContext = {}): boolean {
  return evaluatePassword(password, context).every((rule) => rule.met)
}
